import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import { useDispatch, useSelector } from 'react-redux'
import ContentDeliveries from '../../../pages/documents/accounts/ContentDeliveries'
import {
  Cargo,
  removeCargosFromStore,
  setCargosGroup,
  setCurrentCargo,
  setIsOnlyCargo,
  setIsVisibleDeleteManyModalCargo,
  setIsVisibleDeleteModalCargo,
  setIsVisibleModalCargo,
  setSelectedCargosForDeletion,
  setVisibleContentDeliveries,
  setVisibleSideModal,
} from '../../../redux/reducers/cargos'
import {
  fetchAllInvoices,
  fetchAttachCargo,
  setIsVisibleCreateModalInvoice,
} from '../../../redux/reducers/invoices'
import { cargosSelector } from '../../../redux/selectors/cargos-selector'
import { clientsSelector } from '../../../redux/selectors/clients-selector'
import { invoicesSelector } from '../../../redux/selectors/invoices-selector'
import { AppDispatch } from '../../../redux/store'
import { formatDateToDDMMYYYY } from '../../../utils/date'
import { getAmount, getItemCurrency } from '../../../utils/getAmount'
import { getRole } from '../../../utils/getRole'
import { canCurrentUserDeleteCargo } from '../../../utils/cargoDeletePermission'
import { parseCargoIsPaid } from '../report-modal/cargos/cargoReadinessDate'
import DeleteButton from '../../buttons/actions-button/DeleteButton'
import EditButton from '../../buttons/actions-button/EditButton'
import InvoiceCreateModal from '../invoice-modal/InvoiceCreateModal'
import styles from './CargoSideModal.module.css'

const CargoSideModal = React.memo(function CargoSideModal() {
  const dispatch = useDispatch<AppDispatch>()
  const { isClient } = getRole()

  const [isOpenedDropdown, setOpenedDropdown] = useState(false)

  const {
    isVisibleSideModal,
    currentCargoGroup,
    isVisibleContentDeliveries,
    isVisibleDeleteManyModalCargo,
    cargos,
  } = useSelector(cargosSelector)
  const { allInvoices, isPendingGetAllInvoices, isVisibleCreateModalInvoice } =
    useSelector(invoicesSelector)
  const { clients } = useSelector(clientsSelector)
  const { cargos: cargosData } = useSelector(cargosSelector)

  // Итого к оплате: сумма грузов без счёта (invoice_id === null), по валютам (те же данные, что в панели «К оплате»)
  const totalToPayFromCargos = useMemo(() => {
    const list = Object.values(cargosData || {}).flat()
    const onlyUnpaid = list.filter(
      (c: { invoice_id?: number | null }) => c.invoice_id == null
    )
    const sums: Record<string, number> = {}
    onlyUnpaid.forEach((c: { amount: number; currency?: string }) => {
      const cur = c.currency || 'BYN'
      sums[cur] = (sums[cur] || 0) + Number(c.amount)
    })
    return Object.entries(sums)
      .filter(([, amount]) => amount > 0)
      .map(([currency, amount]) => ({ currency, amount }))
  }, [cargosData])

  const payer = currentCargoGroup?.[0]?.payer === 1
  const isThirdPartyPayer = currentCargoGroup?.[0]?.payer === 2

  const clientId =
    isThirdPartyPayer && currentCargoGroup?.[0]?.third_id
      ? currentCargoGroup[0].third_id
      : payer
        ? currentCargoGroup?.[0]?.to_id
        : currentCargoGroup?.[0]?.from_id

  // Функция для получения имени клиента по ID
  const getClientName = (clientId: number) => {
    const client = clients.find((c) => c.id === clientId)
    return client ? client.entity_name || client.name : 'Неизвестный клиент'
  }

  // Получаем имя для отображения в заголовке
  const getDisplayName = () => {
    if (isThirdPartyPayer && currentCargoGroup?.[0]?.third_id) {
      return getClientName(currentCargoGroup[0].third_id)
    }
    return payer
      ? currentCargoGroup?.[0]?.to?.entity_name
      : currentCargoGroup?.[0]?.from?.entity_name
  }

  // Мемоизируем список счетов для клиента
  const getInvoices = useMemo(
    () => allInvoices.filter((item) => item.client_id === clientId),
    [allInvoices, clientId]
  )

  // Функция для получения актуальных грузов клиента из общего списка
  const getCurrentClientCargos = useCallback(() => {
    if (!clientId || !cargos) return []

    // Если это клиент - получаем все грузы
    if (isClient) {
      return Object.values(cargos).flat()
    }

    // Для обычных пользователей - грузы конкретного клиента
    return cargos[clientId.toString()] || []
  }, [clientId, cargos])

  // Мемоизируем актуальные грузы клиента
  const currentClientCargos = useMemo(
    () => getCurrentClientCargos(),
    [getCurrentClientCargos]
  )

  const [selectedCargos, setSelectedCargos] = useState<number[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState('')
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('')

  const handleCargoSelect = (cargoId: number) => {
    setSelectedCargos((prev) =>
      prev.includes(cargoId)
        ? prev.filter((id) => id !== cargoId)
        : [...prev, cargoId]
    )
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCargos([])
    } else {
      setSelectedCargos((currentCargoGroup ?? []).map((cargo) => cargo.id))
    }
    setSelectAll(!selectAll)
  }

  // Мемоизируем вычисления сумм
  const totalSelectedAmount = useMemo(
    () =>
      (currentCargoGroup ?? [])
        .filter((cargo) => selectedCargos.includes(cargo.id))
        .reduce((sum, cargo) => sum + cargo.amount, 0),
    [currentCargoGroup, selectedCargos]
  )

  const commonTotalSelectedAmount = useMemo(
    () =>
      (currentCargoGroup ?? []).reduce((sum, cargo) => sum + cargo.amount, 0),
    [currentCargoGroup]
  )

  const handleSetInvoice = (id: number, date: string) => {
    setSelectedInvoice(`№${id} от ${formatDateToDDMMYYYY(date)}`)
    setSelectedInvoiceId(String(id))
  }

  const handleEditCargo = (item: any) => {
    dispatch(setCurrentCargo(item))
    dispatch(setIsVisibleModalCargo(true))
    dispatch(setIsOnlyCargo(true))
  }

  const handleDeleteCargo = (item: any) => {
    dispatch(setIsOnlyCargo(true))
    dispatch(setIsVisibleDeleteModalCargo(true))
    dispatch(setCurrentCargo(item))
    setSelectedCargos([])
    setSelectedInvoice('')
  }

  const handleCreateInvoice = () => {
    dispatch(setIsVisibleCreateModalInvoice(true))
    dispatch(setVisibleSideModal(false))
    dispatch(setVisibleContentDeliveries(false))
  }

  const handleAttachInvoice = () => {
    dispatch(
      fetchAttachCargo({ invoiceId: selectedInvoiceId, selectedCargos })
    ).then((res) => {
      //@ts-ignore
      if (!res.error) {
        dispatch(removeCargosFromStore({ cargoIds: selectedCargos }))

        if (currentCargoGroup) {
          const updatedCargoGroup = currentCargoGroup.filter(
            (cargo) => !selectedCargos.includes(cargo.id)
          )
          dispatch(setCargosGroup(updatedCargoGroup))
        }

        setSelectedCargos([])
        setSelectedInvoice('')
        setSelectedInvoiceId('')
      }
    })
  }

  // Мемоизируем функцию получения валюты
  const getCurrency = useCallback(() => {
    return getItemCurrency(currentCargoGroup?.[0]?.currency || '')
  }, [currentCargoGroup])
  useEffect(() => {
    setSelectAll(false)
    setSelectedInvoice('')
  }, [currentCargoGroup, isVisibleSideModal])

  useEffect(() => {
    return () => {
      setOpenedDropdown(false)
      setSelectedInvoice('')
    }
  }, [currentCargoGroup])

  // Очищаем выбранные грузы после успешного массового удаления
  useEffect(() => {
    if (!isVisibleDeleteManyModalCargo) {
      setSelectedCargos([])
      setSelectAll(false)
    }
  }, [isVisibleDeleteManyModalCargo])

  const getCounterpartyInfo = (cargo: Cargo) => {
    const fromName = cargo.from?.entity_name || cargo.from?.name || '—'
    const toName = cargo.to?.entity_name || cargo.to?.name || '—'
    if (cargo.payer === 1) {
      return `${fromName} ⊳`
    }
    return `⊳ ${toName}`
  }

  const handleDeleteMany = () => {
    if (!selectedCargos.length) {
      return
    }
    dispatch(setSelectedCargosForDeletion(selectedCargos))
    dispatch(setIsVisibleDeleteManyModalCargo(true))
  }

  useEffect(() => {
    if (!currentCargoGroup?.length) {
      dispatch(setVisibleContentDeliveries(false))
      // dispatch(setVisibleSideModal(false))
    }
  }, [currentCargoGroup?.length, dispatch])

  // Обновляем currentCargoGroup при изменении грузов (например, после удаления счета)
  useEffect(() => {
    // Не обновляем если модальное окно не видимо - это может быть из-за операции добавления в счет
    if (!isVisibleSideModal || !isVisibleContentDeliveries) {
      return
    }

    if (currentCargoGroup && currentClientCargos.length > 0) {
      // Сравниваем ID грузов в currentCargoGroup с актуальными грузами
      const currentIds = new Set(currentCargoGroup.map((cargo) => cargo.id))
      const actualIds = new Set(currentClientCargos.map((cargo) => cargo.id))

      // Если есть новые грузы (например, после возврата от счета)
      const newCargos = currentClientCargos.filter(
        (cargo) => !currentIds.has(cargo.id)
      )

      if (newCargos.length > 0) {
        // Обновляем currentCargoGroup, добавляя новые грузы
        const updatedCargoGroup = [...currentCargoGroup, ...newCargos]
        dispatch(setCargosGroup(updatedCargoGroup))
      }

      // Если есть удаленные грузы, удаляем их из currentCargoGroup
      const removedCargos = currentCargoGroup.filter(
        (cargo) => !actualIds.has(cargo.id)
      )
      if (removedCargos.length > 0) {
        const updatedCargoGroup = currentCargoGroup.filter((cargo) =>
          actualIds.has(cargo.id)
        )
        dispatch(setCargosGroup(updatedCargoGroup))
      }
    }
  }, [
    currentClientCargos,
    currentCargoGroup,
    dispatch,
    isVisibleSideModal,
    isVisibleContentDeliveries,
  ])

  // Загружаем счета клиента когда открывается второй слайд (контент клиента)
  useEffect(() => {
    if (isVisibleSideModal && isVisibleContentDeliveries && clientId) {
      dispatch(fetchAllInvoices({ client: clientId.toString() }))
    }
  }, [isVisibleSideModal, isVisibleContentDeliveries, clientId, dispatch])

  useEffect(() => {
    return () => {
      dispatch(setIsOnlyCargo(false))
    }
  }, [dispatch])

  return (
    <>
      <div
        className={classNames(
          styles.slidePanel,
          styles.slideLeft,
          styles.level1,
          {
            [styles.opened]: isVisibleSideModal, // Показываем когда открыта боковая панель
          }
        )}
      >
        <div className={styles.slidePanelTitle}>
          <div className="h4 mt-0 mb-0">К оплате</div>
          <div
            onClick={() => {
              dispatch(setCargosGroup([]))
              dispatch(setVisibleSideModal(false))
              dispatch(setVisibleContentDeliveries(false))
              setSelectedCargos([])
              setSelectedInvoice('')
            }}
            className={styles.closeSlidePanel}
          >
            ×
          </div>
          {totalToPayFromCargos.length > 0 && (
            <div
              className={classNames(
                styles.slidePanelTotalWrap,
                styles.small2,
                'pt-2'
              )}
            >
              {totalToPayFromCargos.map(({ currency, amount }) => (
                <span
                  key={currency}
                  className={classNames(styles.totalToPayBadge, styles.small3)}
                >
                  <b>
                    {getAmount(amount)}
                    &nbsp;{getItemCurrency(currency)}
                  </b>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className={styles.slidePanelContent}>
          <ContentDeliveries />
        </div>
      </div>

      {/* HERE  */}

      <div
        className={classNames(
          styles.slidePanel,
          styles.slideLeft,
          styles.level2,
          {
            [styles.opened]: isVisibleSideModal && isVisibleContentDeliveries,
          }
        )}
      >
        <div className={styles.slidePanelTitle}>
          <div className={classNames(styles.h4, 'mt-0 mb-0')}>
            {getDisplayName()}
          </div>
          <div
            onClick={() => {
              // Проверяем, является ли устройство мобильным
              const isMobile = window.innerWidth < 768

              if (isMobile) {
                // На мобиле: закрываем только второй слайд, возвращаемся к первому
                dispatch(setVisibleContentDeliveries(false))
              } else {
                // На десктопе: закрываем всю боковую панель
                dispatch(setVisibleSideModal(false))
                dispatch(setVisibleContentDeliveries(false))
              }

              setSelectedCargos([])
              setSelectedInvoice('')
            }}
            className={styles.closeSlidePanel}
          >
            ×
          </div>
        </div>

        <div className={styles.slidePanelContent}>
          <div className={classNames(styles.bgWhite, 'pt-1 pb-1 mb-2')}>
            <div className="row align-items-center ml-2 mr-2 mb-2">
              <div className={classNames(styles.small2, 'col pt-1 pb-1 pl-0')}>
                <input
                  type="checkbox"
                  id="shipment_all"
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
                <label htmlFor="shipment_all">Выбрать все</label>
              </div>
              <div className="col pl-1 pr-0" style={{ textAlign: 'right' }}>
                <div className={classNames(styles.colorGray, styles.small2)}>
                  Общая сумма
                </div>
                <b className={styles.nowrap}>
                  {getAmount(commonTotalSelectedAmount)} {getCurrency()}
                </b>
              </div>
            </div>
            {/* <div
              onClick={() => {
                // dispatch(setVisibleSideModal(false))
                dispatch(setIsVisibleModalCargo(true))
                dispatch(setVisibleContentDeliveries(false))
                dispatch(setCurrentCargoGroupIdClient(clientId))
              }}
              className={classNames(
                styles.btn,
                styles.btnSmall2,
                styles.btnOutline,
                styles.btnWide
              )}
            >
              + Добавить груз
            </div> */}
          </div>

          {currentCargoGroup?.map((item) => {
            return (
              <div
                key={item.id}
                className={classNames(
                  styles.bgWhite,
                  styles.shipmentItem,
                  styles.bxShadow,
                  styles.border10,
                  styles.small2,
                  'pt-1 pb-1 mb-2'
                )}
              >
                {/* Первая строка: чекбокс + описание слева, счётчик и иконка фото справа */}
                <div className="row align-items-center ml-1 mr-1 mb-1">
                  <div className="col pr-1 pl-1">
                    <input
                      type="checkbox"
                      id={`shipment_${item.id}`}
                      checked={selectedCargos.includes(item.id)}
                      onChange={() => handleCargoSelect(item.id)}
                    />
                    <label htmlFor={`shipment_${item.id}`}>
                      <span className={classNames(styles.ellipsis2)}>
                        <b>{item.description}</b>
                      </span>
                    </label>
                  </div>
                  <div className="col-auto pr-0 pl-1">
                    {item.images &&
                      Array.isArray(item.images) &&
                      item.images.length > 0 && (
                        <span
                          className={classNames(
                            styles.small3,
                            'd-inline-block',
                            'mr-1'
                          )}
                        >
                          {item.images.length}
                        </span>
                      )}
                    {item.images &&
                      Array.isArray(item.images) &&
                      item.images.length > 0 && (
                        <PhotoProvider
                          className={classNames(
                            styles.photoProvider,
                            'd-inline-block'
                          )}
                        >
                          {item.images.map((photo: any, index: number) => (
                            <PhotoView
                              key={photo.id}
                              src={photo?.image?.lg || photo?.image?.original}
                            >
                              {index === 0 ? (
                                <span
                                  className={classNames(
                                    styles.btn,
                                    styles.btnSmall2,
                                    styles.btnBlueOutline,
                                    styles.btnIcon,
                                    'd-inline-block'
                                  )}
                                >
                                  <i
                                    className={classNames(
                                      styles.icon,
                                      styles.iconPicture
                                    )}
                                  />
                                </span>
                              ) : (
                                <img style={{ display: 'none' }} alt="" />
                              )}
                            </PhotoView>
                          ))}
                        </PhotoProvider>
                      )}
                  </div>
                </div>
                {/* Вторая строка: детали слева, кнопки редактировать/удалить справа */}
                <div className="row align-items-end ml-1 mr-1">
                  <div className="col pl-1">
                    <div className={classNames(styles.small3, 'mb-1')}>
                      {!!item.has_tn && (
                        <span
                          className={classNames(
                            'd-inline-block pl-1 pr-1 mr-1',
                            styles.bgGreen,
                            styles.colorWhite,
                            styles.borderRadius5,
                            styles.small3
                          )}
                        >
                          <b>Т/Н</b>
                        </span>
                      )}
                      {getCounterpartyInfo(item)}
                    </div>
                    <div className={classNames(styles.small3, 'row', 'pb-1')}>
                      <div className="col-auto">
                        <i
                          className={classNames(
                            styles.icon,
                            styles.iconRoute,
                            styles.icon10
                          )}
                        />{' '}
                        <b>{item?.report?.number || 'б/н'}</b> &nbsp;{' '}
                        {item?.report?.departure && (
                          <span
                            className={classNames(
                              styles.colorGray,
                              styles.small3
                            )}
                          >
                            от {formatDateToDDMMYYYY(item?.report?.departure)}
                          </span>
                        )}
                      </div>
                      <div className="col" style={{ textAlign: 'right' }}>
                        <b
                          className={classNames(styles.colorRed, styles.nowrap)}
                        >
                          {getAmount(item.amount ?? 0)}{' '}
                          {getItemCurrency(item.currency)}
                        </b>
                        <br />
                        {parseCargoIsPaid(item.is_paid) && (
                          <i
                            className={classNames(
                              styles.icon,
                              styles.iconLike,
                              styles.filterGreen,
                              styles.icon15,
                              'mr-1'
                            )}
                            title="Оплачено!"
                          />
                        )}
                        <span className={styles.colorGray}>
                          {item.payment_method === 'cash'
                            ? 'Нал'
                            : item.payment_method === 'bank_transfer'
                              ? 'Безнал'
                              : null}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    className="col-auto pl-1 pr-0"
                    style={{ textAlign: 'right' }}
                  >
                    <EditButton
                      onClick={() => {
                        handleEditCargo(item)
                      }}
                    />
                    {!isClient && canCurrentUserDeleteCargo(item) && (
                      <DeleteButton
                        onClick={() => {
                          handleDeleteCargo(item)
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {!isClient && (
          <div
            className={classNames(styles.slidePanelButton, {
              [styles.disabled]: selectedCargos.length === 0,
            })}
          >
            <div className={classNames(styles.small, 'row mb-3')}>
              <div className="col">
                <div className={styles.small3}>Выбрано на сумму</div>
                <b>
                  <span>{getAmount(totalSelectedAmount)}</span>&nbsp;
                  {getCurrency()}
                </b>
              </div>
              <div className="col-auto">
                <button
                  onClick={handleCreateInvoice}
                  disabled={selectedCargos.length === 0}
                  className={classNames(
                    styles.btn,
                    styles.btnSmall,
                    styles.btnWide
                  )}
                >
                  Создать счет
                </button>
              </div>
            </div>

            <div className={styles.small}>Добавить в счёт:</div>

            <div className="row">
              <div className="col pr-1">
                <div
                  onClick={() => setOpenedDropdown(!isOpenedDropdown)}
                  className={classNames(
                    styles.dropdown,
                    styles.small2,
                    'd-block mb-1',
                    isOpenedDropdown && selectedCargos.length !== 0
                      ? styles.opened
                      : ''
                  )}
                  style={{ textAlign: 'left' }}
                >
                  <div
                    className={classNames(
                      styles.btn,
                      styles.btnSmall2,
                      styles.btnWide,
                      styles.btnInput,
                      styles.btnDropdown,
                      styles.nowrap
                    )}
                  >
                    {isPendingGetAllInvoices ? (
                      <span>Загрузка счетов...</span>
                    ) : !!selectedInvoice ? (
                      selectedInvoice
                    ) : (
                      'Выберите счёт'
                    )}
                  </div>
                  <div
                    className={classNames(
                      styles.dropdownContent,
                      styles.dropdownSelect
                    )}
                  >
                    {isPendingGetAllInvoices ? (
                      <div
                        className={styles.small2}
                        style={{ padding: '10px', textAlign: 'center' }}
                      >
                        Загрузка счетов...
                      </div>
                    ) : getInvoices.length > 0 ? (
                      getInvoices.map((item) => (
                        <div
                          key={item.id}
                          data-value={item.id}
                          onClick={() => handleSetInvoice(item.id, item.date)}
                        >
                          №{item.id} от {formatDateToDDMMYYYY(item.date)}
                        </div>
                      ))
                    ) : (
                      <div
                        className={styles.small2}
                        style={{ padding: '10px', textAlign: 'center' }}
                      >
                        Нет доступных счетов
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-auto pl-1">
                <button
                  onClick={handleAttachInvoice}
                  disabled={
                    selectedCargos.length === 0 || !Boolean(selectedInvoiceId)
                  }
                  className={classNames(
                    styles.btn,
                    styles.btnSmall2,
                    styles.btnWide,
                    styles.btnBlueOutline
                  )}
                >
                  Добавить
                </button>
              </div>
            </div>
            {Boolean(selectedCargos.length) && (
              <div className="mt-3">
                <button
                  className={classNames(
                    styles.colorRed,
                    styles.small2,
                    'btn',
                    'btn-link',
                    'p-0',
                    'text-left'
                  )}
                  onClick={handleDeleteMany}
                  style={{
                    border: 'none',
                    background: 'none',
                    color: 'inherit',
                  }}
                >
                  <i
                    className={classNames(
                      styles.icon,
                      styles.iconDelete,
                      styles.icon16,
                      styles.iconRed
                    )}
                  ></i>{' '}
                  Удалить выбранные
                </button>
              </div>
            )}
          </div>
        )}
        {isVisibleCreateModalInvoice && (
          <InvoiceCreateModal
            setSelectedCargos={setSelectedCargos}
            selectedCargos={selectedCargos}
          />
        )}
      </div>
    </>
  )
})

CargoSideModal.displayName = 'CargoSideModal'

export default CargoSideModal
